<template>
  <div class="player" :style="playerStyle">
    <button 
      class="round-btn" 
      @click="togglePlay" 
      :aria-label="playing ? 'Pausar' : 'Reproducir'"  
      :title="playing ? 'Pausar' : 'Reproducir'"
    >
      <img :src="playing ? Pause : Union" alt=""/>
    </button>

    <span class="time" title="Tiempo de grabación">{{ mmss(current) }} / {{ mmss(duration) }}</span>

    <input
      class="seek"
      type="range"
      :max="duration || 0"
      step="0.01"
      :value="current"
      @input="onSeek"
      :style="seekStyle"
    />

    <!-- volumen -->
    <div class="vol-wrap" :class="{ 'vol-vertical': volumen === 'vertical' }">
      <div class="vol-slider" title="Volumen">
        <input
          class="volume-pop"
          type="range"
          min="0"
          max="1"
          step="0.01"
          :value="volume"
          @input="onVolumeChange"
          :style="volumeStyle"
        />
      </div>

      <button 
        class="round-btn-vol" 
        @click="toggleMute"
        :aria-label="muted ? 'Activar sonido' : 'Silenciar'"
        :title="muted ? 'Activar sonido' : 'Silenciar'"
      >
        <img :src="muted ? VolumeSlash : VolumeHigh" alt=""/>
      </button>
      
    </div>

    <!-- velocidad -->
    <a-dropdown 
      v-if="!hideVelocity"
      v-model:open="isOpenOptions" 
      :trigger="['click']" 
      placement="bottomLeft"
    >
      <a-button size="small" class="speed-btn" title="Velocidad de reproducción">
        {{ speedLabel }} <component :is="isOpenOptions ? UpOutlined : DownOutlined" :style="{fontSize: '10px',}"/>
      </a-button>
      <template #overlay>
        <a-menu @click="onRateClick">
          <a-menu-item 
            v-for="r in rateItems" 
            :key="r.value" 
            :style="speed === r.value 
              ? { backgroundColor: '#E6F4FF', color: '#262626E0', fontWeight: 600 }
              : null"
          >
            {{ r.label }}
          </a-menu-item>
        </a-menu>
      </template>
    </a-dropdown>
  </div>

  <audio
    ref="audioEl"
    :src="audioUrl"
    preload="metadata"
    @loadedmetadata="onLoaded"
    @timeupdate="onTick"
    @ended="playing = false"
  ></audio>
</template>

<script setup>
import { ref, computed } from "vue";
import {DownOutlined, UpOutlined } from "@ant-design/icons-vue";
import Pause from "@/app/supervision/assets/Pause.png?url";
import Union from "@/app/supervision/assets/Union.png?url";
import VolumeSlash from "@/app/supervision/assets/VolumeSlash.png?url";
import VolumeHigh from "@/app/supervision/assets/VolumeHigh.png?url";

const props = defineProps({
  audioUrl: { type: String, required: true },
  rateItems: {
    type: Array,
    required: true,
    default: [
      { value: 0.25, label: "0.25x" },
      { value: 0.5,  label: "0.5x"  },
      { value: 0.75, label: "0.75x" },
      { value: 1,    label: "1x (Normal)" },
      { value: 1.25, label: "1.25x" },
      { value: 1.5,  label: "1.5x"  },
      { value: 1.75, label: "1.75x" },
      { value: 2,    label: "2x"    },
    ],
  },
  volumen: {
    type: String,
    required: false,
    default: "horizontal", // horizontal | vertical
  },
  playerStyle: {
    type: Object,
    required: false,
    default: ''
  },
  hideVelocity: {
    type: Boolean,
    required: false,
    default: false
  },
});

const audioEl = ref(null);
const playing = ref(false);
const muted = ref(false);
const current = ref(0);
const duration = ref(0);
const isOpenOptions = ref(false);
const speed = ref(1);
const volume = ref(1);        
const lastVolume = ref(1); 

const emit = defineEmits(["metadata"]);

const speedLabel = computed(() => (speed.value === 1 ? "1x" : `${speed.value}x`));

const volumePercent = computed(() => Math.round(volume.value * 100));
const volumeStyle = computed(() => {
  const dir = props.volumen === "vertical" ? "to top" : "to right";
  return {
    background: `linear-gradient(${dir}, #1677FF ${volumePercent.value}%, #BAE0FF ${volumePercent.value}%)`,
  };
});

const percent = computed(() => (duration.value ? (current.value / duration.value) * 100 : 0));
const seekStyle = computed(() => ({
  background: `linear-gradient(to right, #1677FF ${percent.value}%, #91CAFF ${percent.value}%)`,
}));

const mmss = (s) => {
  const x = Math.max(0, Math.floor(s));
  const m = Math.floor(x / 60);
  const ss = String(x % 60).padStart(2, "0");
  return `${m}:${ss}`;
};

const applyRate = () => { if (audioEl.value) audioEl.value.playbackRate = speed.value; };

const togglePlay = () => {
  if (!audioEl.value) return;
  if (audioEl.value.paused) { audioEl.value.play(); playing.value = true; }
  else { audioEl.value.pause(); playing.value = false; }
};

const onSeek = (e) => { 
  const t = Number(e.target.value); 
  if (audioEl.value) audioEl.value.currentTime = t; 
  current.value = t; 
};

const onVolumeChange = (e) => {
  const v = Number(e.target.value);
  volume.value = v;
  if (audioEl.value) {
    audioEl.value.volume = v;
    audioEl.value.muted = v === 0;
  }
  muted.value = v === 0;
  if (v > 0) lastVolume.value = v;
};

const toggleMute = () => {
  if (!audioEl.value) return;
  if (!muted.value) {
    lastVolume.value = volume.value || 1;
    volume.value = 0;
    audioEl.value.volume = 0;
    audioEl.value.muted = true;
    muted.value = true;
  } else {
    const v = lastVolume.value || 1;
    volume.value = v;
    audioEl.value.volume = v;
    audioEl.value.muted = false;
    muted.value = false;
  }
};

const onRateClick = ({ key }) => { speed.value = Number(key); applyRate(); };

const onTick = () => { current.value = audioEl.value?.currentTime || 0; };

const onLoaded = () => {
  duration.value = audioEl.value?.duration || 0;
  if (audioEl.value) audioEl.value.volume = volume.value;
  applyRate();
  emit("metadata", { duration: duration.value, label: mmss(duration.value) });
};
</script>

<style scoped lang="sass">
.player
  display: flex
  align-items: center
  gap: 14px
  padding: 16px
  border-radius: 16px
  background: $blue-1
  position: relative
  overflow: visible

.round-btn
  display: inline-flex
  align-items: center
  justify-content: center
  width: 40px
  height: 40px
  border-radius: 50%
  border: 0
  background: $blue-1
  cursor: pointer
  transition: transform .08s ease, box-shadow .12s ease
  &:hover
    background: #FFFFFFAD
    box-shadow: 0 2px 6px rgba(0,0,0,0.08)
  &:active
    transform: scale(0.98)

.time
  min-width: 69px
  font-size: 14px
  color: #262626E0

.seek
  flex: 1
  min-width: 81px
  height: 6px
  border-radius: 32px
  appearance: none
  background: #91CAFF
  outline: none

.seek::-webkit-slider-runnable-track
  height: 6px
  border-radius: 32px

.seek::-webkit-slider-thumb
  appearance: none
  width: 12px
  height: 12px
  border-radius: 32px
  background: $primary
  margin-top: -4px
  opacity: 0

.seek:hover::-webkit-slider-thumb
  opacity: 1

.vol-wrap
  display: inline-flex
  align-items: center
  height: 40px
  border-radius: 80px
  background: $blue-1
  transition: background .12s ease, box-shadow .12s ease
  position: relative

.vol-wrap:has(.round-btn-vol:hover),
.vol-wrap:has(.vol-slider:hover)
  background: #FFFFFFAD
  box-shadow: 0 2px 8px rgba(0,0,0,.08)

.vol-slider
  max-width: 0
  opacity: 0
  margin-left: 0
  pointer-events: none
  transition: max-width .18s ease, opacity .18s ease, margin-left .18s ease

/* Solo se muestra cuando el hover nace en el boton de volumen. */
.vol-wrap:has(.round-btn-vol:hover) .vol-slider,
.vol-wrap:has(.vol-slider:hover) .vol-slider
  max-width: 114px
  opacity: 1
  margin-left: 8px
  pointer-events: auto

/* 🔹 Reset base para la barra de volumen vertical */
:deep(.volume-pop)
  -webkit-appearance: none
  appearance: none
  background: #1677FF
  border-radius: 32px
  outline: none
  cursor: pointer
  accent-color: #1677FF

/* —— WebKit (Chrome, Edge, Brave) —— */
:deep(.volume-pop::-webkit-slider-runnable-track)
  -webkit-appearance: none
  background: transparent !important
  height: 8px
  border-radius: 32px

:deep(.volume-pop::-webkit-slider-thumb)
  -webkit-appearance: none
  appearance: none
  width: 12px
  height: 12px
  border-radius: 50%
  background: #1677FF
  margin-top: -2px
  cursor: pointer

/* —— Firefox —— */
:deep(.volume-pop::-moz-range-track)
  background: transparent !important
  border-radius: 32px

:deep(.volume-pop::-moz-range-progress)
  background: #1677FF !important
  border-radius: 32px

:deep(.volume-pop::-moz-range-thumb)
  width: 12px
  height: 12px
  border-radius: 50%
  background: #1677FF
  border: none
  cursor: pointer

.round-btn-vol
  display: inline-flex
  align-items: center
  justify-content: center
  width: 40px
  height: 40px
  border-radius: 50%
  border: 0
  background: transparent
  cursor: pointer
  transition: transform .08s ease, box-shadow .12s ease

.speed-btn
  min-width: 52px
  height: 40px
  border: 0
  border-radius: 8px
  background: $blue-1
  font-size: 16px
  &:hover
    background: #FFFFFFAD
    color: #262626E0

.vol-vertical
  width: 40px
  height: 40px
  position: relative
  display: inline-flex
  align-items: center
  justify-content: center
  background: transparent
  box-shadow: none

  &::before
    content: ""
    position: absolute
    bottom: 0
    left: 50%
    transform: translateX(-50%)
    width: 40px
    height: 40px
    border-radius: 999px
    background: $blue-1
    transition: width .18s ease, height .18s ease, border-radius .18s ease, background .12s ease, box-shadow .18s ease
    z-index: 0

  .round-btn-vol
    position: relative
    z-index: 2

  .vol-slider
    position: absolute
    left: 50%
    bottom: 26px
    transform: translateX(-50%)
    width: 64px
    padding: 12px 0
    display: flex
    align-items: center
    justify-content: center
    opacity: 0
    visibility: hidden
    pointer-events: none
    z-index: 2
    transition: opacity .16s ease, visibility .16s step-end

  &:has(.round-btn-vol:hover)::before,
  &:has(.vol-slider:hover)::before
    width: 48px
    height: 145px
    border-radius: 32px
    background: rgba(235, 246, 255, 1)
    box-shadow: 0 8px 20px rgba(0,0,0,.12)

  .volume-pop
    width: 8px
    height: 90px
    border-radius: 32px
    outline: none
    -webkit-appearance: slider-vertical
    writing-mode: bt-lr
    display: block
    margin: 0 auto

    // @supports not (-webkit-appearance: slider-vertical)
    //   -webkit-appearance: none
    //   writing-mode: horizontal-tb
    //   width: 110px
    //   height: 8px
    //   transform: rotate(-90deg)
    //   transform-origin: center
    //   margin: 0 auto

  .volume-pop::-webkit-slider-runnable-track
    height: 8px
    border-radius: 32px
    background: transparent

  .volume-pop::-webkit-slider-thumb
    appearance: none
    width: 12px
    height: 12px
    border-radius: 50%
    background: $primary
    border: 0
    margin-top: -2px

  &:has(.round-btn-vol:hover) .vol-slider,
  &:has(.vol-slider:hover) .vol-slider
    opacity: 1
    visibility: visible
    pointer-events: auto

/* evita que el modo vertical herede la transición de ancho del modo horizontal */
.vol-vertical.vol-wrap:has(.round-btn-vol:hover) .vol-slider,
.vol-vertical.vol-wrap:has(.vol-slider:hover) .vol-slider
  max-width: none !important
  margin-left: 0 !important
</style>




